import type { Appointment } from "../types/appointments";

/**
 * Shared scheduling maths for the day view and the booking-conflict check.
 *
 * `duration` is treated as authoritative rather than (end_time - start_time).
 * Measured on production 2026-08-09: all 10,532 appointments in the last 90
 * days carry a duration (only 30 or 45 minutes, no nulls), but 369 of them —
 * 3.5% — have an end_time that disagrees with it, most likely reschedules that
 * moved one field and not the other. Deriving the end from start + duration
 * keeps a block's drawn height and its conflict window consistent.
 */

export const DAY_START_HOUR = 7; // 7am
export const DAY_END_HOUR = 21; // 9pm
export const SLOT_MINUTES = 30;
export const DEFAULT_DURATION = 30;

/** Minutes past midnight for an appointment's start, or null if untimed. */
export function startMinutes(appointment: Pick<Appointment, "time">): number | null {
  const raw = appointment?.time;
  if (!raw || typeof raw !== "string") return null;
  const [h, m] = raw.split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

export function durationMinutes(
  appointment: Pick<Appointment, "duration">,
): number {
  const d = Number(appointment?.duration);
  return Number.isFinite(d) && d > 0 ? d : DEFAULT_DURATION;
}

export function endMinutes(
  appointment: Pick<Appointment, "time" | "duration">,
): number | null {
  const s = startMinutes(appointment);
  return s === null ? null : s + durationMinutes(appointment);
}

/** Half-open overlap: [aStart, aEnd) vs [bStart, bEnd). Touching ≠ overlapping. */
export function rangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Statuses that no longer occupy the calendar and can't cause a conflict. */
const FREED_STATUSES = new Set(["cancelled", "no_show", "completed"]);

export function occupiesSlot(appointment: Pick<Appointment, "status">): boolean {
  return !FREED_STATUSES.has(String(appointment?.status || "").toLowerCase());
}

/**
 * Appointments on `date` that overlap the proposed [time, time+duration).
 * `ignoreId` lets an appointment be edited without conflicting with itself.
 */
export function findConflicts(
  appointments: Appointment[] | undefined,
  date: string,
  time: string,
  duration: number,
  ignoreId?: string,
): Appointment[] {
  if (!appointments?.length || !date || !time) return [];
  const [h, m] = time.split(":").map(Number);
  if (Number.isNaN(h)) return [];
  const propStart = h * 60 + (Number.isNaN(m) ? 0 : m);
  const propEnd = propStart + (duration > 0 ? duration : DEFAULT_DURATION);

  return appointments.filter((a) => {
    if (!a || a.id === ignoreId) return false;
    if (!occupiesSlot(a)) return false;
    const aDate = typeof a.date === "string" ? a.date.split("T")[0] : String(a.date ?? "");
    if (aDate !== date) return false;
    const s = startMinutes(a);
    if (s === null) return false;
    return rangesOverlap(propStart, propEnd, s, s + durationMinutes(a));
  });
}

export function formatMinutes(mins: number): string {
  const h24 = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Solid block colours per status — the shared statusColors map is bg/text
 *  utility pairs for badges, which read too pale as calendar blocks. */
export const STATUS_BLOCK: Record<string, { bar: string; bg: string; text: string; label: string }> = {
  scheduled: { bar: "bg-blue-500", bg: "bg-blue-50 border-blue-200", text: "text-blue-900", label: "Scheduled" },
  pending: { bar: "bg-blue-500", bg: "bg-blue-50 border-blue-200", text: "text-blue-900", label: "Pending" },
  confirmed: { bar: "bg-emerald-500", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-900", label: "Confirmed" },
  completed: { bar: "bg-emerald-600", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-900", label: "Completed" },
  rescheduled: { bar: "bg-amber-500", bg: "bg-amber-50 border-amber-200", text: "text-amber-900", label: "Rescheduled" },
  no_show: { bar: "bg-orange-500", bg: "bg-orange-50 border-orange-200", text: "text-orange-900", label: "No show" },
  cancelled: { bar: "bg-red-500", bg: "bg-red-50 border-red-200", text: "text-red-900", label: "Cancelled" },
  overdue: { bar: "bg-red-500", bg: "bg-red-50 border-red-200", text: "text-red-900", label: "Overdue" },
};

export function blockStyle(status?: string) {
  return STATUS_BLOCK[String(status || "scheduled").toLowerCase()] || STATUS_BLOCK.scheduled;
}

/**
 * Lay overlapping appointments out side by side.
 *
 * Sweep the day in start order; anything overlapping the running cluster joins
 * it, and each member gets an equal-width column. Without this, two 2pm
 * appointments would render exactly on top of each other and one would be
 * invisible.
 */
export function layoutDay(appointments: Appointment[]): Array<{
  appointment: Appointment;
  start: number;
  end: number;
  column: number;
  columns: number;
}> {
  const timed = appointments
    .map((a) => ({ appointment: a, start: startMinutes(a), end: endMinutes(a) }))
    .filter((x): x is { appointment: Appointment; start: number; end: number } =>
      x.start !== null && x.end !== null)
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const out: Array<{ appointment: Appointment; start: number; end: number; column: number; columns: number }> = [];
  let cluster: typeof timed = [];
  let clusterEnd = -1;

  const flush = () => {
    if (!cluster.length) return;
    // Greedy column assignment within the cluster.
    const colEnds: number[] = [];
    const assigned = cluster.map((item) => {
      let col = colEnds.findIndex((e) => e <= item.start);
      if (col === -1) {
        colEnds.push(item.end);
        col = colEnds.length - 1;
      } else {
        colEnds[col] = item.end;
      }
      return { ...item, column: col };
    });
    for (const item of assigned) out.push({ ...item, columns: colEnds.length });
    cluster = [];
    clusterEnd = -1;
  };

  for (const item of timed) {
    if (cluster.length && item.start >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  flush();
  return out;
}

/**
 * The lead an appointment belongs to, if any.
 *
 * `leadId` is set on newer rows; older ones only carry it inside
 * `associations`, so both have to be checked. Shared by the day view and the
 * calendar's day list so a click behaves the same in either place.
 */
export function getLeadInfo(
  appointment: Pick<Appointment, "leadId" | "associations">,
): { id?: string; name?: string } {
  const assoc = appointment?.associations?.find((a) => a.entityType === "Lead");
  return {
    id: appointment?.leadId || assoc?.entityId,
    name: (assoc as { entityName?: string } | undefined)?.entityName,
  };
}
